// src/components/SinglePost.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BlueskyService } from '../services/bluesky';
import { BookmarkService } from '../services/BookmarkService';
import { Storage } from '../utils/storage';
import Post from './Post';
import { ArrowLeft, Loader2 } from 'lucide-react';

const SinglePost = () => {
  const { handle, id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bluesky] = useState(() => new BlueskyService());
  const [bookmarkService] = useState(() => new BookmarkService());
  const [session] = useState(Storage.getSession());

  useEffect(() => {
    let isMounted = true;

    const fetchPost = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await bluesky.getPost(handle, id);

        if (isMounted) {
          if (response.success && response.data) {
            setPost(response.data);
          } else {
            setError(response.error || 'Failed to load post');
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Error loading post');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    if (handle && id) {
      fetchPost();
    }

    return () => {
      isMounted = false;
    };
  }, [handle, id, bluesky]);

  const handleLike = async (uri, cid, isLiked) => {
    if (!session) {
      navigate('/');
      return;
    }

    try {
      const response = isLiked 
        ? await bluesky.unlikePost(uri, cid)
        : await bluesky.likePost(uri, cid);
      
      if (response.success) {
        const updatedPost = await bluesky.getPost(handle, id);
        if (updatedPost.success) {
          setPost(updatedPost.data);
        }
      }
    } catch (err) {
      // Silently handle error - toast could be added here
    }
  };

  if (isLoading) {
    return (
      <div className="single-post-page">
        <div className="loading">
          <Loader2 size={20} className="loading-spinner" />
          Loading post...
        </div>
      </div>
    );
  }

  return (
    <div className="single-post-page">
      <div className="navigation-header">
        <button 
          className="button button-back"
          onClick={() => navigate('/')}
        >
          <ArrowLeft size={18} />
          Back to Feed
        </button>
      </div>

	  <div className="single-post-container">
		{error ? (
		  <div className="error-container">
			<div className="error-message">{error}</div>
			<button 
			  className="button button-secondary"
			  onClick={() => navigate('/')}
			>
			  Return to Feed
			</button>
		  </div>
		) : post ? (
		  <Post
			post={post}
			bluesky={bluesky}
			bookmarkService={bookmarkService}
			isLoggedIn={!!session}
			onLoginRequired={() => navigate('/')}
			onLike={handleLike}
		  />
		) : (
		  <div className="error-container">
			<div className="error-message">Post not found</div>
			<button 
			  className="button button-secondary"
			  onClick={() => navigate('/')}
			>
			  Return to Feed
			</button>
		  </div>
		)}
	  </div>
	</div>
  );
};

export default SinglePost;